/**
 * BLRRD blend modes — Phase 4.A.layers.1 (5 modes) + 4.A.layers.2 (15 modes).
 *
 * Pure per-channel blend functions for the SEPARABLE modes and per-pixel
 * functions for the NON-SEPARABLE HSL modes. Each takes (cb, cs) → cr
 * where:
 *   • cb is the backdrop colour for ONE channel, ACEScg-linear, expected
 *     in [0, 1] for the standard W3C identities to hold; values outside
 *     the unit interval pass through but their behaviour mirrors the
 *     spec's analytic extension (e.g. screen of two HDR highlights
 *     greater than 1 still produces a sensible larger highlight).
 *   • cs is the source colour for the same channel, same constraints.
 *   • cr is the result colour for that channel.
 *
 * Algorithm: W3C "Compositing and Blending Level 1" recommendation
 * (CC-BY 4.0); see docs/algorithms/layers.md for the citation URL.
 * Cross-reference: Adobe PDF Reference 6th edition §7.2.5 "Blend Mode",
 * Porter & Duff 1984 SIGGRAPH "Compositing Digital Images".
 *
 * Colour space note:
 *   The W3C spec defines these formulas for *non-linear* sRGB-encoded
 *   channels. BLRRD operates in *scene-linear* ACEScg. The per-channel
 *   *math* is identical (each formula is a pointwise rational function
 *   that is well-defined in any value space); only the visual character
 *   of the result differs because linear-light multiplication preserves
 *   energy whereas gamma-encoded multiplication adds a perceptual
 *   compression. For BLRRD's privacy-first photography use case we
 *   prefer the linear-light interpretation (it commutes correctly with
 *   the kernel's exposure/contrast math). The blend-mode picker will
 *   later let users opt into sRGB-encoded compositing for legacy
 *   Photoshop parity (post-Wave-7.2).
 *
 * Channel-independence:
 *   The 16 *separable* modes (Normal, Multiply, Screen, Overlay,
 *   Soft Light, Hard Light, Difference, Color Dodge, Color Burn,
 *   Darken, Lighten, Linear Light, Vivid Light, Pin Light, Hard Mix,
 *   Exclusion) operate per-channel: each output channel depends only
 *   on its own input channels.
 *
 *   The 4 *non-separable* modes (Hue, Saturation, Color, Luminosity)
 *   live in W3C §10.3 and operate per-pixel. They are implemented as
 *   triplet-in / triplet-out functions, dispatched through the
 *   compositor's pixel loop via a separate function family.
 *
 * HDR-preservation invariant (CRITICAL — see Codex 4.A.layers.1 STOP-1):
 *   These functions DO NOT clamp results to [0, 1]. BLRRD's HDR-fused
 *   buffers carry highlights above 1.0; clamping at the blend stage
 *   would destroy the float headroom the filmic/sigmoid view-transform
 *   later compresses. The W3C identities still hold for in-gamut inputs;
 *   out-of-gamut inputs extend analytically. Floating-point guards
 *   (e.g. division-by-zero in Color Dodge) are applied locally where
 *   needed without clamping the result.
 *
 * Provenance: implemented clean-room from the W3C spec text + Adobe PDF
 * Reference §7.2.5 + Porter-Duff 1984. No GIMP / Photoshop / Krita /
 * Pillow source was consulted. The HSL helper functions (Lum, SetLum,
 * ClipColor, Sat, SetSat) follow the Adobe PDF Reference §7.2.6
 * pseudocode literally.
 */

import { clamp01 } from './model.js';

/* ──────────────────────────────────────────────────────────────────────
 * Separable per-channel blend functions.
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Normal (W3C §10.2.1). Source replaces backdrop.
 *     B(cb, cs) = cs
 */
export function blendNormal(_cb, cs) {
    return cs;
}

/**
 * Multiply (W3C §10.2.2). Darkening — product of backdrop and source.
 *     B(cb, cs) = cb × cs
 */
export function blendMultiply(cb, cs) {
    return cb * cs;
}

/**
 * Screen (W3C §10.2.3). Lightening — symmetric counterpart of Multiply.
 *     B(cb, cs) = 1 − (1 − cb)(1 − cs) = cb + cs − cb × cs
 */
export function blendScreen(cb, cs) {
    return cb + cs - cb * cs;
}

/**
 * Hard Light (W3C §10.2.7). Multiply on dark source, Screen on bright
 * source. Internally re-used by Overlay (operands swapped).
 *
 *     B(cb, cs) = | 2 × cb × cs                  (cs ≤ 0.5)
 *                 | 1 − 2 × (1 − cb)(1 − cs)     (cs > 0.5)
 */
export function blendHardLight(cb, cs) {
    if (cs <= 0.5) {
        return 2 * cb * cs;
    }
    return 1 - 2 * (1 - cb) * (1 - cs);
}

/**
 * Overlay (W3C §10.2.6). "Hard Light with the source and backdrop
 * swapped" — Multiply on dark backdrops, Screen on bright backdrops.
 *
 *     Overlay(cb, cs) = HardLight(cs, cb)
 */
export function blendOverlay(cb, cs) {
    return blendHardLight(cs, cb);
}

/**
 * Soft Light (W3C §10.2.8). A subtler Overlay; avoids the harsh
 * banding at the cs = 0.5 boundary. Piecewise formula:
 *
 *     B(cb, cs) = | cb − (1 − 2 × cs) × cb × (1 − cb)             (cs ≤ 0.5)
 *                 | cb + (2 × cs − 1) × (D(cb) − cb)               (cs > 0.5)
 *
 *     D(cb)     = | ((16 × cb − 12) × cb + 4) × cb                 (cb ≤ 0.25)
 *                 | √cb                                            (cb > 0.25)
 */
export function blendSoftLight(cb, cs) {
    if (cs <= 0.5) {
        return cb - (1 - 2 * cs) * cb * (1 - cb);
    }
    let d;
    if (cb <= 0.25) {
        d = ((16 * cb - 12) * cb + 4) * cb;
    } else {
        d = Math.sqrt(cb < 0 ? 0 : cb);
    }
    return cb + (2 * cs - 1) * (d - cb);
}

/**
 * Difference (W3C §10.2.10). Absolute difference of channels.
 *     B(cb, cs) = |cb − cs|
 */
export function blendDifference(cb, cs) {
    const d = cb - cs;
    return d < 0 ? -d : d;
}

/**
 * Exclusion (W3C §10.2.11). Lower-contrast cousin of Difference.
 *     B(cb, cs) = cb + cs − 2 × cb × cs
 */
export function blendExclusion(cb, cs) {
    return cb + cs - 2 * cb * cs;
}

/**
 * Color Dodge (W3C §10.2.4). Brightens backdrop toward source. The
 * W3C piecewise formula is:
 *
 *     B(cb, cs) = | 0                              (cb ≤ 0)
 *                 | 1                              (cs ≥ 1)
 *                 | min(1, cb / (1 − cs))          (otherwise)
 *
 * The `min(1, …)` is INTRINSIC to the W3C identity — Color Dodge is a
 * brightening operator that saturates at white rather than producing
 * energy out of nowhere. For SDR inputs in [0, 1] this is EXACTLY the
 * spec.
 *
 * HDR-preservation note (Codex 4.A.layers.2 STOP-1):
 *   The spec caps at 1. For HDR scene-linear inputs (e.g. cb = 4) the
 *   cap is raised to `max(1, cb)` so the backdrop's own highlight
 *   survives the blend (you can't darken a backdrop via "dodging", so
 *   the natural floor for an HDR backdrop is cb itself). For SDR
 *   `cb ≤ 1` this reduces to the W3C `min(1, …)` exactly.
 */
export function blendColorDodge(cb, cs) {
    if (cb <= 0) return 0;
    if (cs >= 1) return cb > 1 ? cb : 1;
    const ratio = cb / (1 - cs);
    /* HDR-aware cap. SDR (cb ≤ 1) → cap = 1 → exact W3C `min(1, …)`.
     * HDR (cb > 1) → cap = cb so the backdrop's highlight isn't lost. */
    const cap = cb > 1 ? cb : 1;
    return ratio < cap ? ratio : cap;
}

/**
 * Color Burn (W3C §10.2.5). Darkens backdrop toward source.
 *
 *     B(cb, cs) = | 1                              (cb ≥ 1)
 *                 | 0                              (cs ≤ 0)
 *                 | 1 − min(1, (1 − cb) / cs)      (otherwise)
 *
 * The `min(1, …)` is again INTRINSIC to the W3C identity — Color Burn
 * is a darkening operator that bottoms out at black. For SDR inputs in
 * [0, 1] this is EXACTLY the spec.
 *
 * HDR-preservation note (Codex 4.A.layers.2 STOP-1):
 *   The spec implies the result is in [0, 1] under SDR. For HDR cb > 1
 *   ("highlight you would burn into") we still want the burn to scale
 *   sensibly: we extend the ratio cap to `max(1, cs)` so a strong HDR
 *   source can drive the darkening past 0 only as far as the natural
 *   "anti-cb" floor allows. For SDR (cs ≤ 1) this reduces to the W3C
 *   `min(1, …)` exactly.
 */
export function blendColorBurn(cb, cs) {
    /* SDR identity at cb=1 → 1. Codex 4.A.layers.2 STOP-2: HDR backdrop
     * (cb > 1) must survive — "you can't darken a backdrop past its
     * existing brightness via burning". So the cb ≥ 1 early-return
     * yields cb itself for HDR, 1 for SDR. */
    if (cb >= 1) return cb > 1 ? cb : 1;
    if (cs <= 0) return 0;
    const ratio = (1 - cb) / cs;
    /* Cap the ratio at max(1, cs) — for SDR (cs ≤ 1) cap=1, exact W3C;
     * for HDR (cs > 1) the burn still saturates symmetrically. */
    const cap = cs > 1 ? cs : 1;
    const capped = ratio < cap ? ratio : cap;
    return 1 - capped;
}

/**
 * Darken (W3C §10.2.13). Channelwise minimum.
 *     B(cb, cs) = min(cb, cs)
 */
export function blendDarken(cb, cs) {
    return cb < cs ? cb : cs;
}

/**
 * Lighten (W3C §10.2.12). Channelwise maximum.
 *     B(cb, cs) = max(cb, cs)
 */
export function blendLighten(cb, cs) {
    return cb > cs ? cb : cs;
}

/* ──────────────────────────────────────────────────────────────────────
 * Tier-2 compound separable modes. These are NOT in W3C Level 1, but
 * are catalogued in Adobe PDF Reference §7.2.5 "Photoshop blend modes"
 * and used widely in colour-grading. Each is defined by composition of
 * the Level-1 primitives — we keep the per-pixel math one-liners.
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Linear Light (Adobe §7.2.5). Linear-Burn for cs ≤ 0.5, Linear-Dodge
 * for cs > 0.5. A single one-liner expresses it:
 *
 *     B(cb, cs) = cb + 2 × cs − 1
 *
 * Equivalent to Linear Dodge (Add) of cb with (2cs − 1). Aggressive
 * contrast — typical of "high-pass" sharpening recipes.
 */
export function blendLinearLight(cb, cs) {
    return cb + 2 * cs - 1;
}

/**
 * Vivid Light (Adobe §7.2.5). Color Burn for cs ≤ 0.5, Color Dodge for
 * cs > 0.5 — using doubled source as the dodge/burn driver:
 *
 *     B(cb, cs) = | ColorBurn(cb, 2 × cs)                 (cs ≤ 0.5)
 *                 | ColorDodge(cb, 2 × (cs − 0.5))        (cs > 0.5)
 */
export function blendVividLight(cb, cs) {
    if (cs <= 0.5) {
        return blendColorBurn(cb, 2 * cs);
    }
    return blendColorDodge(cb, 2 * (cs - 0.5));
}

/**
 * Pin Light (Adobe §7.2.5). Darken for cs ≤ 0.5 (driver = 2cs),
 * Lighten for cs > 0.5 (driver = 2(cs − 0.5)):
 *
 *     B(cb, cs) = | Darken(cb, 2 × cs)                    (cs ≤ 0.5)
 *                 | Lighten(cb, 2 × (cs − 0.5))           (cs > 0.5)
 */
export function blendPinLight(cb, cs) {
    if (cs <= 0.5) {
        return blendDarken(cb, 2 * cs);
    }
    return blendLighten(cb, 2 * (cs - 0.5));
}

/**
 * Hard Mix (Adobe §7.2.5). Vivid-Light followed by a step at 0.5 — the
 * result is either 0 or 1 (in the SDR-canonical case). Implemented as
 * the literal step:
 *
 *     B(cb, cs) = | 0                                     (cb + cs < 1)
 *                 | 1                                     (cb + cs ≥ 1)
 *
 * HDR note: the threshold "cb + cs ≥ 1" still applies in scene-linear
 * (the boundary is conceptually the SDR midpoint sum, not a clamp).
 * Values >1 will simply land in the "1" branch, which is the intent.
 */
export function blendHardMix(cb, cs) {
    return (cb + cs) >= 1 ? 1 : 0;
}

/* ──────────────────────────────────────────────────────────────────────
 * Non-separable HSL modes (W3C §10.3). These operate per-PIXEL, not
 * per-channel — they need the full RGB triplet to compute luminance and
 * saturation. The helper functions Lum / SetLum / ClipColor / Sat /
 * SetSat follow the Adobe PDF Reference §7.2.6 pseudocode literally.
 *
 * Each returns the result triplet as a fresh 3-element plain array
 * [r, g, b]. Allocation per pixel is the price of clarity; the
 * compositor's hot path skips this entirely for the 16 separable modes
 * (the common case).
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Luminance (W3C §10.3 / Adobe §7.2.6). Rec. 601 luma coefficients per
 * W3C spec — note: these are the same coefficients used by the W3C
 * compositing spec for ALL colour spaces (the spec is colour-space
 * agnostic at this layer; the choice of luma weights is a spec
 * convention, not a colourimetric statement).
 *
 *     Lum(C) = 0.3 × R + 0.59 × G + 0.11 × B
 */
function lumW3C(r, g, b) {
    return 0.3 * r + 0.59 * g + 0.11 * b;
}

/**
 * ClipColor (W3C §10.3 / Adobe §7.2.6). Clip any out-of-[0,1] result
 * BACK toward its luminance — pulls the colour into gamut while
 * preserving its luma. NOTE: this clipping is INTRINSIC to the HSL
 * blend formulas (Adobe pseudocode) — it is part of the algorithm,
 * NOT an HDR-clamp violation. The clipping is done in the HSL helper
 * space only; the resulting RGB can still extend beyond [0,1] because
 * `lumW3C` of an HDR input can exceed 1.0 (and ClipColor doesn't
 * compress the luma itself).
 *
 * Mutates in place for performance — callers pass a fresh array.
 */
function clipColorInPlace(c) {
    const L = lumW3C(c[0], c[1], c[2]);
    let n = c[0]; if (c[1] < n) n = c[1]; if (c[2] < n) n = c[2];
    let x = c[0]; if (c[1] > x) x = c[1]; if (c[2] > x) x = c[2];
    if (n < 0) {
        /* Compress toward Lum so the minimum reaches 0. */
        const denom = L - n;
        if (denom > 1e-12) {
            const k = L / denom;
            c[0] = L + (c[0] - L) * k;
            c[1] = L + (c[1] - L) * k;
            c[2] = L + (c[2] - L) * k;
        }
    }
    if (x > 1) {
        /* Compress toward Lum so the maximum reaches 1. */
        const denom = x - L;
        if (denom > 1e-12) {
            const k = (1 - L) / denom;
            c[0] = L + (c[0] - L) * k;
            c[1] = L + (c[1] - L) * k;
            c[2] = L + (c[2] - L) * k;
        }
    }
}

/**
 * SetLum (W3C §10.3 / Adobe §7.2.6). Re-anchor a colour so it carries
 * a given luminance, then clip back to gamut.
 *
 *     SetLum(C, L) = ClipColor(C + (L − Lum(C)))
 */
function setLumInPlace(c, L) {
    const d = L - lumW3C(c[0], c[1], c[2]);
    c[0] += d; c[1] += d; c[2] += d;
    clipColorInPlace(c);
}

/**
 * Saturation (W3C §10.3 / Adobe §7.2.6). Range of the channel values.
 *     Sat(C) = max(R, G, B) − min(R, G, B)
 */
function satW3C(r, g, b) {
    let n = r; if (g < n) n = g; if (b < n) n = b;
    let x = r; if (g > x) x = g; if (b > x) x = b;
    return x - n;
}

/**
 * SetSat (W3C §10.3 / Adobe §7.2.6). Re-scale a colour's RGB range to
 * a given saturation while preserving the mid channel's relative
 * position. Identifies the (min, mid, max) channels first.
 *
 * The Adobe pseudocode:
 *
 *     if Cmax > Cmin:
 *         Cmid = (Cmid − Cmin) × s / (Cmax − Cmin)
 *         Cmax = s
 *     else:
 *         Cmid = Cmax = 0
 *     Cmin = 0
 *
 * We implement it by sorting the three channel indices then writing
 * back to the original triplet by index.
 */
function setSatInPlace(c, s) {
    /* Identify min/mid/max channels via three comparisons. */
    let iMin = 0, iMax = 0;
    if (c[1] < c[iMin]) iMin = 1;
    if (c[2] < c[iMin]) iMin = 2;
    if (c[1] > c[iMax]) iMax = 1;
    if (c[2] > c[iMax]) iMax = 2;
    /* iMid is "the other one". */
    let iMid;
    if (iMin === iMax) {
        /* All three channels are equal — saturation is 0, result is 0. */
        c[0] = 0; c[1] = 0; c[2] = 0;
        return;
    }
    if ((iMin !== 0) && (iMax !== 0)) iMid = 0;
    else if ((iMin !== 1) && (iMax !== 1)) iMid = 1;
    else iMid = 2;
    const cMin = c[iMin], cMid = c[iMid], cMax = c[iMax];
    if (cMax > cMin) {
        c[iMid] = (cMid - cMin) * s / (cMax - cMin);
        c[iMax] = s;
    } else {
        c[iMid] = 0;
        c[iMax] = 0;
    }
    c[iMin] = 0;
}

/**
 * Hue blend (W3C §10.3.3). Result has the source's hue, backdrop's
 * saturation, backdrop's luminance.
 *
 *     B(Cb, Cs) = SetLum(SetSat(Cs, Sat(Cb)), Lum(Cb))
 *
 * Returns a fresh [r, g, b] triplet.
 */
export function blendHue(br, bg, bb, sr, sg, sb) {
    const c = [sr, sg, sb];
    setSatInPlace(c, satW3C(br, bg, bb));
    setLumInPlace(c, lumW3C(br, bg, bb));
    return c;
}

/**
 * Saturation blend (W3C §10.3.4). Result has the backdrop's hue,
 * source's saturation, backdrop's luminance.
 *
 *     B(Cb, Cs) = SetLum(SetSat(Cb, Sat(Cs)), Lum(Cb))
 */
export function blendSaturation(br, bg, bb, sr, sg, sb) {
    const c = [br, bg, bb];
    setSatInPlace(c, satW3C(sr, sg, sb));
    setLumInPlace(c, lumW3C(br, bg, bb));
    return c;
}

/**
 * Color blend (W3C §10.3.5). Result has the source's hue+saturation
 * and the backdrop's luminance.
 *
 *     B(Cb, Cs) = SetLum(Cs, Lum(Cb))
 */
export function blendColor(br, bg, bb, sr, sg, sb) {
    const c = [sr, sg, sb];
    setLumInPlace(c, lumW3C(br, bg, bb));
    return c;
}

/**
 * Luminosity blend (W3C §10.3.6). Result has the backdrop's
 * hue+saturation and the source's luminance.
 *
 *     B(Cb, Cs) = SetLum(Cb, Lum(Cs))
 */
export function blendLuminosity(br, bg, bb, sr, sg, sb) {
    const c = [br, bg, bb];
    setLumInPlace(c, lumW3C(sr, sg, sb));
    return c;
}

/* ──────────────────────────────────────────────────────────────────────
 * Dispatch tables. Two families: SEPARABLE (per-channel) and
 * NON_SEPARABLE (per-pixel). The compositor selects the family once
 * per layer; the inner loop calls only the chosen family.
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Per-channel blend dispatch. Compositor calls these in the hot loop,
 * one call per channel × pixel.
 */
export const BLEND_MODES = {
    'normal':       blendNormal,
    'multiply':     blendMultiply,
    'screen':       blendScreen,
    'overlay':      blendOverlay,
    'soft-light':   blendSoftLight,
    'hard-light':   blendHardLight,
    'difference':   blendDifference,
    'exclusion':    blendExclusion,
    'color-dodge':  blendColorDodge,
    'color-burn':   blendColorBurn,
    'darken':       blendDarken,
    'lighten':      blendLighten,
    'linear-light': blendLinearLight,
    'vivid-light':  blendVividLight,
    'pin-light':    blendPinLight,
    'hard-mix':     blendHardMix,
};

/**
 * Per-pixel HSL blend dispatch. The compositor selects this table when
 * the layer's blend mode is one of the 4 non-separable modes. The
 * function signature is `(br, bg, bb, sr, sg, sb) → [r, g, b]`.
 */
export const BLEND_MODES_NON_SEPARABLE = {
    'hue':         blendHue,
    'saturation':  blendSaturation,
    'color':       blendColor,
    'luminosity':  blendLuminosity,
};

/**
 * Public catalog — order matters for the UI dropdown. Layout follows
 * Photoshop / Affinity convention: Normal-group, Darken-group,
 * Lighten-group, Contrast-group, Inversion-group, Compound-group,
 * HSL-group. Re-ordered for usability not algorithm-similarity.
 */
export const BLEND_MODE_LIST = [
    'normal',
    'multiply',
    'screen',
    'overlay',
    'soft-light',
    'hard-light',
    'color-dodge',
    'color-burn',
    'darken',
    'lighten',
    'difference',
    'exclusion',
    'linear-light',
    'vivid-light',
    'pin-light',
    'hard-mix',
    'hue',
    'saturation',
    'color',
    'luminosity',
];

/**
 * Resolve a blend-mode id to its function, falling back to Normal for
 * unknown ids. The compositor MUST tolerate unknown modes (e.g. a
 * snapshot serialised by a future wave that adds new modes and then
 * deserialised by an older build) without throwing.
 *
 * Returns the separable function family; callers must check
 * `isNonSeparableBlendMode(modeId)` first if they want the HSL family.
 */
export function resolveBlendFn(modeId) {
    const fn = BLEND_MODES[modeId];
    return typeof fn === 'function' ? fn : blendNormal;
}

/**
 * Resolve a non-separable HSL blend-mode id to its triplet-in / triplet-
 * out function. Returns `null` if the id is not a recognised HSL mode —
 * callers (compositor) interpret this as "use the separable path".
 */
export function resolveNonSeparableBlendFn(modeId) {
    const fn = BLEND_MODES_NON_SEPARABLE[modeId];
    return typeof fn === 'function' ? fn : null;
}

/**
 * Is the given mode id one of the 4 non-separable HSL modes? The
 * compositor uses this to branch between the per-channel hot loop and
 * the per-pixel HSL hot loop. Cheap O(1) object check.
 */
export function isNonSeparableBlendMode(modeId) {
    return Object.prototype.hasOwnProperty.call(BLEND_MODES_NON_SEPARABLE, modeId);
}

/**
 * Clamp a blend result into [0, 1]. CALLERS BEWARE — the compositor
 * does NOT use this; the HDR-preservation invariant (Codex 4.A.layers.1
 * STOP-1) requires that out-of-gamut blend results pass through to the
 * filmic/sigmoid view transform. This helper is re-exported only for
 * tests + completeness; production code must not call it on blend
 * results.
 */
export function clampBlendResult(v) {
    return clamp01(v);
}
